import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { playable } from '../../components/FreePlayNote';
import './Keno4Hearts.css'; // reuse core styles (board cells, modals, win flash)
import './SuperDoubleUp.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* Betohalics Super Double-Up Keno — spec V1.
 * Two-phase paid round: start → reveal balls 1-10 → decide keep/double
 *   → finish → reveal balls 11-19 + SUPERBALL (ball 20).
 * Bonus: doubleUpChosen && superballHit ⇒ 5 Super Free Plays (retrigger +2).
 * Superball hit multiplies the qualifying payout by 4×. */

const STATE = {
    READY: 'READY',
    FIRST_DRAW: 'FIRST_DRAW',
    DOUBLE_UP_DECISION: 'DOUBLE_UP_DECISION',
    SECOND_DRAW: 'SECOND_DRAW',
    SUPERBALL_PRE: 'SUPERBALL_PRE',
    SUPERBALL_REVEAL: 'SUPERBALL_REVEAL',
    BONUS_TRIGGER: 'BONUS_TRIGGER',
    SUPER_FREE_PLAY: 'SUPER_FREE_PLAY',
    BONUS_COMPLETE: 'BONUS_COMPLETE',
    ROUND_COMPLETE: 'ROUND_COMPLETE',
};

/* ---- Sound effects (Web Audio API) ------------------------------------
 * Tiny inline SFX module. Standalone from Keno4Hearts so this game can be
 * lifted into its own bundle if we ever code-split. Every sound is
 * synthesized on the fly — no asset downloads, no audio tag warm-up delay.
 */
let _actx = null;
const _ctx = () => {
    if (typeof window === 'undefined') return null;
    if (_actx) { if (_actx.state === 'suspended') _actx.resume().catch(()=>{}); return _actx; }
    try {
        const AC = window.AudioContext || window.webkitAudioContext;
        _actx = new AC();
    } catch { return null; }
    return _actx;
};
const _tone = (ctx, { f=440, f2=null, dur=0.1, type='sine', gain=0.14, atk=0.005, rel=0.05 }) => {
    const now = ctx.currentTime;
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.type = type; o.frequency.setValueAtTime(f, now);
    if (f2 !== null) o.frequency.exponentialRampToValueAtTime(Math.max(1, f2), now + dur);
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(gain, now + atk);
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur + rel);
    o.connect(g).connect(ctx.destination);
    o.start(now); o.stop(now + dur + rel + 0.02);
};
const _burst = (ctx, { dur=0.3, gain=0.2, filt=800, ftype='lowpass' }) => {
    const now = ctx.currentTime;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    const s = ctx.createBufferSource(); s.buffer = buf;
    const f = ctx.createBiquadFilter(); f.type = ftype; f.frequency.value = filt;
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, now);
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    s.connect(f).connect(g).connect(ctx.destination);
    s.start(now); s.stop(now + dur + 0.02);
};
const sfx = (name) => {
    const c = _ctx(); if (!c) return;
    try {
        switch (name) {
            case 'ball_drop':
                return _tone(c, { f: 580, f2: 300, dur: 0.05, type: 'sine', gain: 0.09 });
            case 'ball_hit':
                return _tone(c, { f: 1100, f2: 1700, dur: 0.08, type: 'triangle', gain: 0.16 });
            case 'keep_bet':
                _tone(c, { f: 500, f2: 380, dur: 0.09, type: 'triangle', gain: 0.12 });
                setTimeout(()=>_tone(_ctx(), { f: 420, dur: 0.09, type: 'sine', gain: 0.1 }), 60);
                return;
            case 'double_up_zap': {
                // Magenta electric chime — quick rising chord + sizzle noise.
                _tone(c, { f: 660, f2: 1320, dur: 0.14, type: 'sawtooth', gain: 0.14 });
                _tone(c, { f: 880, f2: 1760, dur: 0.14, type: 'square', gain: 0.09 });
                _burst(c, { dur: 0.2, gain: 0.18, filt: 3200, ftype: 'bandpass' });
                setTimeout(() => {
                    const c2 = _ctx(); if (!c2) return;
                    _tone(c2, { f: 1320, f2: 2200, dur: 0.12, type: 'triangle', gain: 0.13 });
                }, 90);
                return;
            }
            case 'superball_reveal': {
                // Cyan chime — bright triad.
                _tone(c, { f: 880,  dur: 0.4, type: 'triangle', gain: 0.14, rel: 0.25 });
                _tone(c, { f: 1320, dur: 0.4, type: 'sine',     gain: 0.11, rel: 0.25 });
                setTimeout(() => _tone(_ctx(), { f: 1760, dur: 0.35, type: 'triangle', gain: 0.12, rel: 0.25 }), 90);
                return;
            }
            case 'bonus_ball_slam': {
                // Heavier magenta slam for the Double-Up bonus ball reveal.
                _tone(c, { f: 220, f2: 40, dur: 0.55, type: 'sawtooth', gain: 0.32, rel: 0.3 });
                _burst(c, { dur: 0.5, gain: 0.28, filt: 400, ftype: 'lowpass' });
                _burst(c, { dur: 0.3, gain: 0.16, filt: 5200, ftype: 'highpass' });
                setTimeout(() => {
                    const c2 = _ctx(); if (!c2) return;
                    _tone(c2, { f: 1200, f2: 1800, dur: 0.18, type: 'triangle', gain: 0.18 });
                    _tone(c2, { f: 1600, f2: 2400, dur: 0.18, type: 'square', gain: 0.11 });
                }, 200);
                setTimeout(() => {
                    const c3 = _ctx(); if (!c3) return;
                    _tone(c3, { f: 180, f2: 30, dur: 0.35, type: 'sawtooth', gain: 0.2 });
                }, 320);
                return;
            }
            case 'bonus_trigger': {
                // Big fanfare when 5 free plays are awarded.
                const scale = [523, 659, 784, 1046, 1318];
                scale.forEach((f, i) => setTimeout(() => _tone(_ctx(), { f, dur: 0.4, type: 'triangle', gain: 0.16, rel: 0.25 }), i * 80));
                return;
            }
            case 'win_chime':
                _tone(c, { f: 1046, dur: 0.25, type: 'triangle', gain: 0.16 });
                setTimeout(() => _tone(_ctx(), { f: 1318, dur: 0.35, type: 'triangle', gain: 0.14 }), 100);
                return;
            default: return;
        }
    } catch { /* audio best-effort */ }
};

const SuperDoubleUp = () => {
    const { user } = useAuth();
    const [wallet, setWallet] = useState(null);
    const balance = wallet ?? playable(user);
    const updateCredits = setWallet;
    useEffect(() => { setWallet(null); }, [user?.credits, user?.bonus_credits]);
    const navigate = useNavigate();

    const [config, setConfig] = useState(null);
    const [progressive, setProgressive] = useState({ pool: 0 });
    const [selected, setSelected] = useState([]);
    const [betIdx, setBetIdx] = useState(2);
    const [gameState, setGameState] = useState(STATE.READY);
    const [roundId, setRoundId] = useState(null);
    const [revealed, setRevealed] = useState([]); // committed draws revealed so far
    const [firstTen, setFirstTen] = useState([]);
    const [finishResult, setFinishResult] = useState(null);
    const [session, setSession] = useState(null); // active bonus session
    const [bonusOverlay, setBonusOverlay] = useState(null);
    const [winFlash, setWinFlash] = useState(null);
    const [lastWin, setLastWin] = useState(0);
    const [showHelp, setShowHelp] = useState(false);
    const [showPaytable, setShowPaytable] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [history, setHistory] = useState(null); // null=loading, []=empty
    const [expandedRound, setExpandedRound] = useState(null);
    const [helpPage, setHelpPage] = useState(0);
    const [bonusTotals, setBonusTotals] = useState(null);
    const [doubleChosen, setDoubleChosen] = useState(false); // true after user selects DOUBLE UP; also true during a bonus/free-play session

    // -------- Auto-Play --------
    const [showAutoConfig, setShowAutoConfig] = useState(false);
    const [autoMode, setAutoMode] = useState('keep'); // 'keep' | 'double' | 'ask'
    const [autoRounds, setAutoRounds] = useState(10); // planned count; 0 = infinite
    const [autoStopWin, setAutoStopWin] = useState(''); // stop if a single win >= this
    const [autoStopBalance, setAutoStopBalance] = useState(''); // stop if balance < this
    const [autoRunning, setAutoRunning] = useState(false);
    const [autoRemaining, setAutoRemaining] = useState(0);
    const [autoTotals, setAutoTotals] = useState({ wagered: 0, won: 0, rounds: 0 });
    const autoRef = useRef({
        running: false, mode: 'keep', planned: 10, stopWin: null, stopBal: null,
        remaining: 0, wagered: 0, won: 0, rounds: 0, startedAt: null, startingWager: 0,
    });
    const finalizingAuto = useRef(false);
    const selectedSetRef = useRef(new Set());
    useEffect(() => { selectedSetRef.current = new Set(selected); }, [selected]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/keno-super-double/config`, { withCredentials: true });
                setConfig(data);
                if (data.progressive) setProgressive(data.progressive);
            } catch { toast.error('Could not load config'); }
            try {
                const { data } = await axios.get(`${API}/keno-super-double/session`, { withCredentials: true });
                if (data.session) {
                    setSession(data.session);
                    setSelected(data.session.locked_numbers);
                    setDoubleChosen(true);
                    setGameState(STATE.SUPER_FREE_PLAY);
                }
            } catch { /* no session */ }
        })();
    }, []);

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            if (!alive) return;
            try {
                const { data } = await axios.get(`${API}/keno-super-double/progressive`, { withCredentials: true });
                if (alive) setProgressive(p => ({ ...p, ...data }));
            } catch { /* silent */ }
        };
        const id = setInterval(tick, 4000);
        return () => { alive = false; clearInterval(id); };
    }, []);

    const bet = config ? config.bet_levels[betIdx] : 1;
    const doubleWager = bet * 2;
    const canPlay = gameState === STATE.READY && !session
        && selected.length >= 2 && selected.length <= 10
        && balance >= bet;

    const toggle = useCallback((n) => {
        if (gameState !== STATE.READY || session) return;
        setSelected(prev => {
            if (prev.includes(n)) return prev.filter(x => x !== n);
            if (prev.length >= 10) { toast.error('Max 10 spots'); return prev; }
            return [...prev, n];
        });
    }, [gameState, session]);

    const quickPick = useCallback(() => {
        if (gameState !== STATE.READY || session) return;
        const target = selected.length >= 2 ? selected.length : 8;
        const pool = Array.from({ length: 80 }, (_, i) => i + 1);
        for (let i = pool.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [pool[i], pool[j]] = [pool[j], pool[i]];
        }
        setSelected(pool.slice(0, target));
    }, [gameState, session, selected.length]);

    const clearCard = useCallback(() => {
        if (gameState !== STATE.READY || session) return;
        setSelected([]);
    }, [gameState, session]);

    const openHistory = useCallback(async () => {
        setShowHistory(true);
        setHistory(null);
        setExpandedRound(null);
        try {
            const { data } = await axios.get(`${API}/keno-super-double/history`, { withCredentials: true });
            setHistory(data.rounds || []);
        } catch {
            setHistory([]);
            toast.error('Could not load history');
        }
    }, []);

    const cycleBet = (dir) => {
        if (gameState !== STATE.READY || session) return;
        setBetIdx(i => Math.max(0, Math.min(config.bet_levels.length - 1, i + dir)));
    };

    const revealBallsSequential = async (balls) => {
        // Comfortable pace: 140ms per ball (10 balls in ~1.4s).
        const interval = 140;
        for (let i = 0; i < balls.length; i++) {
            await new Promise(r => setTimeout(r, interval));
            const n = balls[i];
            setRevealed(prev => [...prev, n]);
            if (selectedSetRef.current.has(n)) sfx('ball_hit');
            else sfx('ball_drop');
        }
    };

    const startPaidRound = useCallback(async () => {
        if (!canPlay) return;
        setLastWin(0);
        setRevealed([]);
        setFinishResult(null);
        setBonusOverlay(null);
        setWinFlash(null);
        try {
            const { data } = await axios.post(`${API}/keno-super-double/start`, {
                selected_numbers: selected,
                starting_wager: bet,
            }, { withCredentials: true });
            setRoundId(data.round_id);
            setFirstTen(data.first_ten);
            updateCredits?.(data.balance_after);
            setGameState(STATE.FIRST_DRAW);
            await revealBallsSequential(data.first_ten);
            setGameState(STATE.DOUBLE_UP_DECISION);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not start round');
            setGameState(STATE.READY);
        }
    }, [canPlay, selected, bet, updateCredits, config]);

    const decide = useCallback(async (action) => {
        if (gameState !== STATE.DOUBLE_UP_DECISION || !roundId) return;
        if (action === 'double') { setDoubleChosen(true); sfx('double_up_zap'); }
        else { sfx('keep_bet'); }
        try {
            const { data } = await axios.post(`${API}/keno-super-double/finish`, {
                round_id: roundId, action,
            }, { withCredentials: true });
            setFinishResult(data);
            setGameState(STATE.SECOND_DRAW);
            const second9 = data.second_ten.slice(0, 9);
            await revealBallsSequential(second9);
            setGameState(STATE.SUPERBALL_PRE);
            // Longer buildup when it's the BONUS BALL — real tension before the reveal.
            const isBonus = (action === 'double');
            const prePause = isBonus
                ? (config.timings.bonusBallPrePause || 1500)
                : (config.timings.superballPrePause || 700);
            await new Promise(r => setTimeout(r, prePause));
            setGameState(STATE.SUPERBALL_REVEAL);
            setRevealed(prev => [...prev, data.superball]);
            sfx(isBonus ? 'bonus_ball_slam' : 'superball_reveal');
            const revealDur = isBonus
                ? (config.timings.bonusBallRevealDuration || 900)
                : (config.timings.superballRevealDuration || 700);
            await new Promise(r => setTimeout(r, revealDur));

            updateCredits?.(data.balance_after);
            setLastWin(data.final_base_payout + data.progressive_award);
            if (data.bonus_triggered) {
                setGameState(STATE.BONUS_TRIGGER);
                sfx('bonus_trigger');
                setBonusOverlay({
                    headline: 'SUPER DOUBLE-UP BONUS!',
                    subtext: '5 SUPER FREE PLAYS AWARDED',
                    freePlays: 5,
                });
                await new Promise(r => setTimeout(r, config.timings.bonusTriggerDuration || 2500));
                setBonusOverlay(null);
                try {
                    const s = await axios.get(`${API}/keno-super-double/session`, { withCredentials: true });
                    setSession(s.data.session);
                } catch { /* ignore */ }
                setGameState(STATE.SUPER_FREE_PLAY);
            } else {
                if (data.final_base_payout > 0) {
                    sfx('win_chime');
                    const tier = data.final_base_payout >= data.active_wager * 100 ? 'mega'
                              : data.final_base_payout >= data.active_wager * 25 ? 'huge'
                              : data.final_base_payout >= data.active_wager * 5 ? 'big' : 'nice';
                    setWinFlash({ amount: data.final_base_payout + data.progressive_award, tier });
                    setTimeout(() => setWinFlash(null), 2200);
                }
                setGameState(STATE.ROUND_COMPLETE);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Round failed');
            setGameState(STATE.READY);
        }
    }, [gameState, roundId, config, updateCredits]);

    const playFreeRound = useCallback(async () => {
        if (!session) return;
        setLastWin(0);
        setRevealed([]);
        setGameState(STATE.SECOND_DRAW);
        try {
            const { data } = await axios.post(`${API}/keno-super-double/free-play`, {
                session_id: session.session_id,
            }, { withCredentials: true });
            const first19 = data.drawn.slice(0, 19);
            await revealBallsSequential(first19);
            setGameState(STATE.SUPERBALL_PRE);
            // Bonus-ball drama also applies to free-play rounds (they're always doubled by definition).
            const prePause = (config.timings.bonusBallPrePause || 1500);
            await new Promise(r => setTimeout(r, prePause));
            setGameState(STATE.SUPERBALL_REVEAL);
            setRevealed(prev => [...prev, data.superball]);
            sfx('bonus_ball_slam');
            await new Promise(r => setTimeout(r, config.timings.bonusBallRevealDuration || 900));
            updateCredits?.(data.balance_after);
            setLastWin(data.final_base_payout + data.progressive_award);
            if (data.final_base_payout > 0) {
                sfx('win_chime');
                const tier = data.final_base_payout >= session.locked_active_wager * 100 ? 'mega'
                          : data.final_base_payout >= session.locked_active_wager * 25 ? 'huge'
                          : data.final_base_payout >= session.locked_active_wager * 5 ? 'big' : 'nice';
                setWinFlash({ amount: data.final_base_payout + data.progressive_award, tier });
                setTimeout(() => setWinFlash(null), 2200);
            }
            if (data.retrigger_triggered) {
                sfx('bonus_trigger');
                setBonusOverlay({ headline: 'SUPERBALL HIT!', subtext: '+2 FREE PLAYS', freePlays: 2 });
                await new Promise(r => setTimeout(r, config.timings.superballRetriggerDuration || 1500));
                setBonusOverlay(null);
            }
            if (data.session_completed) {
                setBonusTotals(data.bonus_totals);
                setSession(null);
                setGameState(STATE.BONUS_COMPLETE);
            } else {
                setSession(s => ({ ...s, free_plays_remaining: data.free_plays_after }));
                setGameState(STATE.SUPER_FREE_PLAY);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Free play failed');
            setGameState(STATE.SUPER_FREE_PLAY);
        }
    }, [session, config, updateCredits]);

    /* Auto-chain free plays: once a bonus is active and the current free
     * play settles, kick off the next one automatically. Player only taps
     * PLAY FREE once — the bonus rolls itself until BONUS COMPLETE. */
    useEffect(() => {
        if (gameState !== STATE.SUPER_FREE_PLAY) return;
        if (!session || session.free_plays_remaining <= 0) return;
        // Only chain if we just finished a round (revealed has all 20 balls).
        if (revealed.length !== 20) return;
        const t = setTimeout(() => {
            setRevealed([]);
            setLastWin(0);
            setTimeout(() => playFreeRound(), 120);
        }, 1200); // brief pause so the player sees the win/retrigger
        return () => clearTimeout(t);
    }, [gameState, session, revealed.length, playFreeRound]);

    const collectAndContinue = () => {
        setBonusTotals(null);
        setDoubleChosen(false);
        setGameState(STATE.READY);
        setRevealed([]);
        setFinishResult(null);
        setRoundId(null);
    };

    const nextRound = () => {
        setRevealed([]);
        setFinishResult(null);
        setRoundId(null);
        setLastWin(0);
        setDoubleChosen(false);
        setGameState(STATE.READY);
    };

    // ---------------- Auto-Play controller ----------------
    const finalizeAutoPlay = useCallback(async (stopReason) => {
        if (finalizingAuto.current) return;
        finalizingAuto.current = true;
        const a = autoRef.current;
        a.running = false;
        setAutoRunning(false);
        try {
            await axios.post(`${API}/keno-super-double/autoplay-log`, {
                mode: a.mode,
                planned_rounds: a.planned || 1,
                rounds_played: a.rounds,
                stop_reason: stopReason,
                starting_wager: a.startingWager,
                total_wagered: Number(a.wagered.toFixed(2)),
                total_won: Number(a.won.toFixed(2)),
                started_at: a.startedAt,
                stop_on_win_threshold: a.stopWin,
                stop_on_balance_threshold: a.stopBal,
            }, { withCredentials: true });
        } catch { /* audit log best-effort */ }
        const net = a.won - a.wagered;
        const netStr = (net >= 0 ? '+' : '−') + '$' + Math.abs(net).toFixed(2);
        toast(`Auto-Play stopped · ${a.rounds} round${a.rounds===1?'':'s'} · ${netStr}`, {
            description: stopReason.replace(/_/g,' '),
        });
        finalizingAuto.current = false;
    }, []);

    const startAutoPlay = useCallback(() => {
        if (!canPlay || autoRef.current.running) return;
        const planned = autoRounds > 0 ? autoRounds : 9999;
        const stopWin = autoStopWin === '' ? null : Number(autoStopWin);
        const stopBal = autoStopBalance === '' ? null : Number(autoStopBalance);
        autoRef.current = {
            running: true, mode: autoMode, planned,
            stopWin: (stopWin && stopWin > 0) ? stopWin : null,
            stopBal: (stopBal && stopBal > 0) ? stopBal : null,
            remaining: planned, wagered: 0, won: 0, rounds: 0,
            startedAt: new Date().toISOString(),
            startingWager: bet,
        };
        setAutoRunning(true);
        setAutoRemaining(planned);
        setAutoTotals({ wagered: 0, won: 0, rounds: 0 });
        setShowAutoConfig(false);
        // First round is kicked off by the effect below once autoRunning=true settles.
    }, [canPlay, autoMode, autoRounds, autoStopWin, autoStopBalance, bet]);

    const stopAutoPlay = useCallback(() => {
        if (!autoRef.current.running) return;
        finalizeAutoPlay('user_stop');
    }, [finalizeAutoPlay]);

    // Kick off the first paid round once autoRunning=true (avoids stale-closure
    // hazard of firing startPaidRound from a setTimeout inside startAutoPlay).
    useEffect(() => {
        if (!autoRef.current.running) return;
        if (autoRef.current.rounds !== 0) return; // only for the very first round
        if (gameState !== STATE.READY) return;
        const t = setTimeout(() => startPaidRound(), 150);
        return () => clearTimeout(t);
    }, [autoRunning, gameState, startPaidRound]);

    // Auto-decide when the double-up decision overlay appears during auto-play.
    useEffect(() => {
        if (!autoRef.current.running) return;
        if (gameState !== STATE.DOUBLE_UP_DECISION) return;
        const mode = autoRef.current.mode;
        if (mode === 'ask') return; // let user click manually
        const action = mode === 'double' ? 'double' : 'keep';
        // Guard: if double but not enough balance, fall back to keep.
        const chosen = (action === 'double' && balance < bet) ? 'keep' : action;
        const t = setTimeout(() => decide(chosen), 650);
        return () => clearTimeout(t);
    }, [gameState, decide, balance, bet]);

    // Sequence rounds and enforce stop conditions on round completion.
    useEffect(() => {
        if (!autoRef.current.running) return;
        if (gameState !== STATE.ROUND_COMPLETE || !finishResult) return;
        const a = autoRef.current;
        // Tally
        const wagerThisRound = finishResult.active_wager || bet;
        const wonThisRound = (finishResult.final_base_payout || 0) + (finishResult.progressive_award || 0);
        a.wagered += wagerThisRound;
        a.won += wonThisRound;
        a.rounds += 1;
        a.remaining -= 1;
        setAutoTotals({ wagered: a.wagered, won: a.won, rounds: a.rounds });
        setAutoRemaining(a.remaining);
        // Stop conditions — prefer server-authoritative balance from finishResult.
        const currentBalance = (finishResult.balance_after !== undefined) ? finishResult.balance_after : balance;
        let stopReason = null;
        if (a.stopWin && wonThisRound >= a.stopWin) stopReason = 'stop_on_win';
        else if (a.stopBal && currentBalance < a.stopBal) stopReason = 'stop_on_balance';
        else if (currentBalance < bet) stopReason = 'insufficient_balance';
        else if (a.remaining <= 0) stopReason = 'planned_end';
        if (stopReason) {
            finalizeAutoPlay(stopReason);
            return;
        }
        // Continue: schedule next round
        const t = setTimeout(() => {
            nextRound();
            setTimeout(() => startPaidRound(), 200);
        }, 700);
        return () => clearTimeout(t);
    }, [gameState, finishResult, bet, user, finalizeAutoPlay, startPaidRound]);

    // If a bonus session fires while auto-play is running, stop the run.
    useEffect(() => {
        if (!autoRef.current.running) return;
        if (gameState === STATE.SUPER_FREE_PLAY || gameState === STATE.BONUS_TRIGGER) {
            finalizeAutoPlay('bonus_triggered');
        }
    }, [gameState, finalizeAutoPlay]);
    // ---------------- /Auto-Play ----------------

    const selectedSet = useMemo(() => new Set(selected), [selected]);
    const drawnSet = useMemo(() => new Set(revealed), [revealed]);
    const isSuperball = (n) => finishResult && revealed.length === 20 && n === finishResult.superball;

    const paytableSpots = selected.length >= 2 ? selected.length : 10;
    const paytableRows = useMemo(() => {
        if (!config) return [];
        const activeWager = finishResult?.active_wager || (session?.locked_active_wager) || bet;
        const rows = [];
        for (let h = 10; h >= 2; h--) {
            const mult = config.paytable?.[String(paytableSpots)]?.[String(h)] ?? config.paytable?.[paytableSpots]?.[h];
            if (mult) {
                rows.push({ hits: h, multiplier: mult, base: (mult * activeWager).toFixed(2), sb: (mult * activeWager * 4).toFixed(2), top: h === paytableSpots });
            }
        }
        return rows;
    }, [config, paytableSpots, bet, finishResult, session]);

    // End-of-round breakdown — derived from the SAME paytable config the
    // panels use, so the summary, the HITS/WIN panel and the settlement all
    // agree to the cent. Prevents any "I hit more than it paid" disputes.
    const roundSummary = useMemo(() => {
        if (!finishResult || gameState !== STATE.ROUND_COMPLETE) return null;
        const s = selected.length;
        const hits = finishResult.total_hits ?? 0;
        const mult = Number(
            config?.paytable?.[String(s)]?.[String(hits)] ??
            config?.paytable?.[s]?.[hits] ?? 0
        );
        const activeWager = finishResult.active_wager || bet;
        const base = finishResult.base_payout_before_4x ?? 0;
        const superballHit = !!finishResult.superball_hit;
        const finalBase = finishResult.final_base_payout ?? 0;
        const prog = finishResult.progressive_award ?? 0;
        return {
            spots: s, hits, mult, activeWager, base, superballHit, finalBase,
            prog, progTriggered: !!finishResult.progressive_triggered,
            total: finalBase + prog,
        };
    }, [finishResult, gameState, selected.length, config, bet]);

    if (!config) return <div className="keno-4h-page" data-testid="sd-page">Loading…</div>;

    const half1 = Array.from({ length: 40 }, (_, i) => i + 1);
    const half2 = Array.from({ length: 40 }, (_, i) => i + 41);

    const renderCell = (n) => {
        const isSel = selectedSet.has(n);
        const isDrn = drawnSet.has(n);
        const isHit = isSel && isDrn;
        const isSup = isSuperball(n);
        const cls = ['keno-4h-cell', isSel && 'selected', isHit && 'hit',
                     isSup && 'sd-superball', isDrn && !isSel && 'drawn-not-selected'].filter(Boolean).join(' ');
        return (
            <button
                key={n}
                className={cls}
                onClick={() => toggle(n)}
                disabled={gameState !== STATE.READY || !!session}
                data-testid={`sd-cell-${n}`}
            ><span className="num-label">{n}</span></button>
        );
    };

    const activeWagerDisplay = finishResult?.active_wager || session?.locked_active_wager || bet;
    const hitsCount = selected.filter(n => drawnSet.has(n)).length;
    const spots = selected.length;

    // Determine primary CTA state
    const cta = gameState === STATE.READY
        ? { label: 'PLAY GAME', sub: 'HOLD FOR AUTO', action: startPaidRound, disabled: !canPlay, testid: 'sd-play' }
        : gameState === STATE.SUPER_FREE_PLAY
            ? { label: 'PLAY FREE', sub: `${session?.free_plays_remaining ?? 0} LEFT`, action: playFreeRound, testid: 'sd-play-free', electric: true }
            : gameState === STATE.ROUND_COMPLETE
                ? { label: 'CONTINUE', sub: '', action: nextRound, testid: 'sd-continue' }
                : { label: gameState.replace(/_/g, ' '), sub: '', action: null, disabled: true };

    return (
        <div className="keno-4h-page sd-page" data-testid="sd-page">
            <div className="keno-4h-rotate" data-testid="sd-rotate">
                <svg className="icon" viewBox="0 0 64 64" fill="none">
                    <rect x="14" y="8" width="36" height="48" rx="6" stroke="#40c8ff" strokeWidth="3"/>
                    <rect x="18" y="14" width="28" height="36" rx="2" fill="#0a1f45" stroke="#40c8ff" strokeWidth="1.5"/>
                </svg>
                <h1>Rotate Your Phone</h1>
                <p>Super Double-Up Keno is best played sideways.</p>
                <button onClick={() => navigate('/casino/keno')} data-testid="sd-rotate-enter-room">Enter Room</button>
            </div>

            <div className="sd-shell">
                {/* Top bar: back + tiny mobile chips + title + credit/bonus */}
                <div className="sd-topbar">
                    <button onClick={() => navigate('/casino/keno')} className="sd-back-btn" data-testid="sd-back">← Room</button>
                    {/* Mobile-only compact chips (relocated from the hidden left column). */}
                    <div className="sd-topbar-chips" data-testid="sd-topbar-chips">
                        <div className="sd-chip sd-chip-bet">
                            <span className="lbl">BET</span>
                            <span className="val" data-testid="sd-current-bet-chip">${activeWagerDisplay.toFixed(2)}</span>
                        </div>
                        <div className="sd-chip sd-chip-prog">
                            <span className="lbl">JACKPOT</span>
                            <span className="val" data-testid="sd-progressive-chip">${Number(progressive.pool || 0).toLocaleString('en-US',{minimumFractionDigits:0, maximumFractionDigits:0})}</span>
                        </div>
                        {selected.length >= 2 && paytableRows[0] && (
                            <div className="sd-chip sd-chip-top">
                                <span className="lbl">TOP {paytableRows[0].hits}</span>
                                <span className="val">{paytableRows[0].top && spots === 10 ? 'PROG' : `$${paytableRows[0].base}`}</span>
                            </div>
                        )}
                    </div>
                    <div className="sd-title-wrap">
                        <span className="sd-flame left" aria-hidden="true" />
                        <span className="sd-bolt sd-bolt-left" aria-hidden="true">⚡</span>
                        <div className="sd-title-lines">
                            <div className="sd-title-betohalics">BETOHALICS</div>
                            <div className="sd-title-super">SUPER</div>
                            <div className="sd-title-double">DOUBLE-UP</div>
                            <div className="sd-title-keno">KENO</div>
                        </div>
                        <span className="sd-bolt sd-bolt-right" aria-hidden="true">⚡</span>
                        <span className="sd-flame right" aria-hidden="true" />
                    </div>
                    {session && session.free_plays_remaining > 0 ? (
                        <div className="keno-4h-freeplay-banner sd-freeplay-banner-inline" data-testid="sd-freeplay-banner">
                            <div className="fpb-label">BONUS ACTIVE</div>
                            <div className="fpb-count">{session.free_plays_remaining}</div>
                            <div className="fpb-sub">FREE PLAYS LEFT</div>
                        </div>
                    ) : (
                        <div className="sd-topbar-credit" data-testid="sd-topbar-credit">
                            <div className="sd-credit-lbl">CREDIT</div>
                            <div className="sd-credit-val">${balance.toFixed(2)}</div>
                        </div>
                    )}
                </div>

                {/* Left column: current bet + hits/win paytable + progressive */}
                <div className="sd-leftcol" data-testid="sd-leftcol">
                    <div className="sd-panel sd-currentbet">
                        <div className="sd-lbl">CURRENT BET</div>
                        <div className="sd-val" data-testid="sd-current-bet">${activeWagerDisplay.toFixed(2)}</div>
                        <div className="sd-sub">{spots} SPOTS</div>
                    </div>
                    <div className="sd-panel sd-hitswin">
                        <div className="sd-hitswin-head"><span>HITS</span><span>WIN</span></div>
                        <div className="sd-hitswin-rows">
                            {paytableRows.length === 0 ? (
                                <div className="sd-hitswin-empty">Select 2-10 spots</div>
                            ) : paytableRows.map(r => (
                                <div key={r.hits} className={`sd-hitswin-row ${r.top?'top':''} ${hitsCount===r.hits?'live':''}`}>
                                    <span className="hits">{r.hits}</span>
                                    <span className="win">{r.top && spots === 10 ? 'PROGRESSIVE' : `$${r.base}`}</span>
                                </div>
                            ))}
                        </div>
                        <div className="sd-hitswin-foot">
                            LAST BALL IS <strong className={doubleChosen ? 'sd-bonus-label' : ''}>{doubleChosen ? 'BONUS BALL' : 'SUPERBALL'}</strong><br/>
                            <em>{doubleChosen ? 'HIT = 4× + FREE PLAYS' : 'SUPERBALL HIT = 4×'}</em>
                        </div>
                    </div>
                    <div className="sd-panel sd-progressive-card">
                        <div className="sd-coins">
                            <span className="sd-coin" aria-hidden="true"/>
                            <span className="sd-coin" aria-hidden="true"/>
                            <span className="sd-coin" aria-hidden="true"/>
                        </div>
                        <div className="sd-prog-label">PROGRESSIVE<br/>JACKPOT</div>
                        <div className="sd-prog-amount" data-testid="sd-progressive">
                            ${Number(progressive.pool || 0).toLocaleString('en-US',{minimumFractionDigits:2, maximumFractionDigits:2})}
                        </div>
                    </div>
                </div>

                {/* Center column: board + drawn-balls strip + status row */}
                <div className="sd-center">
                    <div className="sd-panel sd-board">
                        <div className="keno-4h-board-half">{half1.map(renderCell)}</div>
                        <div className={`sd-fire-strip ${doubleChosen ? 'is-bonus' : ''}`}>
                            <span className="sd-fire-icon" aria-hidden="true"/>
                            LAST BALL IS SUPERBALL
                            <span className="sd-mult-badge">×4</span>
                            RAISE + SUPERBALL = BONUS ROUND
                            <span className="sd-fire-icon" aria-hidden="true"/>
                        </div>
                        <div className="keno-4h-board-half">{half2.map(renderCell)}</div>
                    </div>

                    <div className="sd-panel sd-drawn-strip">
                        <span className="sd-drawn-label">DRAWN</span>
                        <div className="sd-drawn-balls" data-testid="sd-drawn-column">
                            {Array.from({length:20}).map((_, i) => {
                                const b = revealed[i];
                                const isLastSlot = (i === 19);
                                const isChargingSlot = isLastSlot && gameState === STATE.SUPERBALL_PRE;
                                if (b === undefined) {
                                    return <span key={i} className={`sd-drawn-ball empty ${isChargingSlot ? (doubleChosen ? 'charging bonus' : 'charging') : ''}`}>{isChargingSlot ? (doubleChosen ? '?' : '?') : i+1}</span>;
                                }
                                const superballCls = isLastSlot ? (doubleChosen ? 'sd-superball-ball sd-bonus-ball' : 'sd-superball-ball') : '';
                                return <span key={i} className={`sd-drawn-ball filled ${superballCls}`}>{b}</span>;
                            })}
                        </div>
                    </div>

                    {(gameState === STATE.SUPERBALL_PRE || gameState === STATE.SUPERBALL_REVEAL) && (
                        <div className="sd-superball-note" data-testid="sd-superball-note">
                            SUPERBALL ×4 pays only when the last ball is one of <strong>YOUR</strong> picked numbers
                        </div>
                    )}

                    <div className="sd-status-row">
                        <div className="sd-status-card">
                            <div className="sd-status-lbl">SELECTED</div>
                            <div className="sd-status-val" data-testid="sd-status-selected">{spots}</div>
                        </div>
                        <div className="sd-status-card">
                            <div className="sd-status-lbl">HITS</div>
                            <div className="sd-status-val" data-testid="sd-status-hits">{hitsCount}</div>
                        </div>
                        <div className="sd-status-card win">
                            <div className="sd-status-lbl">WIN</div>
                            <div className="sd-status-val" data-testid="sd-win">${lastWin.toFixed(2)}</div>
                        </div>
                    </div>

                    {roundSummary && (
                        <div className={`sd-result-strip ${roundSummary.total > 0 ? 'win' : ''}`} data-testid="sd-result-strip">
                            <span className="sd-rs-seg">HIT <strong data-testid="sd-rs-hits">{roundSummary.hits}/{roundSummary.spots}</strong></span>
                            <span className="sd-rs-dot">·</span>
                            <span className="sd-rs-seg"><strong data-testid="sd-rs-mult">{roundSummary.mult}×</strong></span>
                            {roundSummary.superballHit && (<><span className="sd-rs-dot">·</span><span className="sd-rs-seg sup">SB ×4</span></>)}
                            {roundSummary.prog > 0 && (<><span className="sd-rs-dot">·</span><span className="sd-rs-seg prog">+JACKPOT</span></>)}
                            <span className="sd-rs-dot">·</span>
                            <span className="sd-rs-seg pay"><strong data-testid="sd-rs-total">${roundSummary.total.toFixed(2)}</strong></span>
                            {finishResult?.round_id && (
                                <button
                                    type="button"
                                    className="sd-rs-copy"
                                    data-testid="sd-rs-copy"
                                    title="Copy receipt for a cashier"
                                    onClick={() => {
                                        const line = `Round ${finishResult.round_id} · ${roundSummary.hits}/${roundSummary.spots} hits · paid $${roundSummary.total.toFixed(2)}`;
                                        try { navigator.clipboard?.writeText(line); toast.success('Receipt copied'); }
                                        catch { toast.error('Copy failed'); }
                                    }}
                                >COPY</button>
                            )}
                        </div>
                    )}
                </div>

                {/* Right column: credit, controls, spots/bet steppers, autoplay, play button */}
                <div className="sd-right">
                    <div className="sd-panel sd-credit-card sd-credit-card-hide-on-mobile">
                        <div className="sd-credit-lbl">CREDIT</div>
                        <div className="sd-credit-val" data-testid="sd-balance">${balance.toFixed(2)}</div>
                    </div>

                    <div className="sd-right-actions">
                        <button className="sd-btn sd-btn-blue" onClick={()=>setShowHelp(true)} data-testid="sd-help">HELP</button>
                        <button className="sd-btn sd-btn-blue" onClick={()=>setShowPaytable(true)} data-testid="sd-paytable">PAYTABLE</button>
                        <button className="sd-btn sd-btn-blue" onClick={openHistory} data-testid="sd-history">HISTORY</button>
                        <button className="sd-btn sd-btn-purple" onClick={quickPick} disabled={!!session || gameState!==STATE.READY} data-testid="sd-qp">QUICK PICK</button>
                        <button className="sd-btn sd-btn-red" onClick={clearCard} disabled={!!session || gameState!==STATE.READY} data-testid="sd-clear">CLEAR</button>
                    </div>

                    <div className="sd-stepper-row-mobile">
                        <div className="sd-stepper">
                            <button className="sd-step-btn" onClick={()=>{
                                if (gameState!==STATE.READY || session) return;
                                setSelected(prev => prev.slice(0, Math.max(0, prev.length-1)));
                            }} data-testid="sd-spots-minus">−</button>
                            <div className="sd-step-body">
                                <div className="sd-step-lbl">SPOTS</div>
                                <div className="sd-step-val" data-testid="sd-spots-count">{spots}</div>
                            </div>
                            <button className="sd-step-btn" onClick={()=>{
                                if (gameState!==STATE.READY || session || spots >= 10) return;
                                const pool = Array.from({length:80}, (_,i)=>i+1).filter(n => !selectedSet.has(n));
                                if (pool.length===0) return;
                                const pick = pool[Math.floor(Math.random()*pool.length)];
                                setSelected(prev => [...prev, pick]);
                            }} data-testid="sd-spots-plus">+</button>
                        </div>

                        <div className="sd-stepper">
                            <button className="sd-step-btn" onClick={()=>cycleBet(-1)} disabled={!!session || gameState!==STATE.READY || betIdx===0} data-testid="sd-bet-minus">−</button>
                            <div className="sd-step-body">
                                <div className="sd-step-lbl">BET</div>
                                <div className="sd-step-val" data-testid="sd-bet">${bet.toFixed(2)}</div>
                            </div>
                            <button className="sd-step-btn" onClick={()=>cycleBet(1)} disabled={!!session || gameState!==STATE.READY || betIdx===config.bet_levels.length-1} data-testid="sd-bet-plus">+</button>
                        </div>
                    </div>

                    <button
                        className={`sd-btn ${autoRunning ? 'sd-btn-red' : 'sd-btn-blue'} sd-autoplay-btn`}
                        data-testid="sd-autoplay"
                        onClick={() => autoRunning ? stopAutoPlay() : setShowAutoConfig(true)}
                        disabled={!autoRunning && (gameState !== STATE.READY || !!session || selected.length < 2)}
                        title={autoRunning ? 'Stop auto-play' : 'Configure auto-play'}
                    >
                        {autoRunning ? `STOP · ${autoRemaining}` : 'AUTO PLAY'}
                    </button>

                    {gameState === STATE.DOUBLE_UP_DECISION ? (
                        <div className="sd-inline-decision" data-testid="sd-decision-overlay">
                            <div className="sd-inline-decision-head">
                                <div className="sd-inline-title">DOUBLE-UP?</div>
                                <div className="sd-inline-sub">${bet.toFixed(2)} <span>→</span> ${doubleWager.toFixed(2)}</div>
                            </div>
                            <button className="sd-inline-btn sd-inline-double" onClick={()=>decide('double')} disabled={balance < bet} data-testid="sd-double-up">
                                <span className="sd-inline-main">⚡ DOUBLE UP ⚡</span>
                                <span className="sd-inline-sub2">+${bet.toFixed(2)} · Ball 20 = BONUS</span>
                            </button>
                            <button className="sd-inline-btn sd-inline-keep" onClick={()=>decide('keep')} data-testid="sd-keep-bet">
                                <span className="sd-inline-main">KEEP BET</span>
                                <span className="sd-inline-sub2">Stay at ${bet.toFixed(2)}</span>
                            </button>
                            {balance < bet && (
                                <p className="sd-inline-warn">Not enough balance to double.</p>
                            )}
                        </div>
                    ) : (
                        <button
                            className={`sd-play-btn ${cta.electric ? 'electric' : ''}`}
                            onClick={cta.action || undefined}
                            disabled={cta.disabled}
                            data-testid={cta.testid || 'sd-cta'}
                        >
                            <span className="sd-play-main">{cta.label}</span>
                            {cta.sub && <span className="sd-play-sub">{cta.sub}</span>}
                        </button>
                    )}
                </div>

                {/* Bottom legend + progressive tip */}
                <div className="sd-bottombar">
                    <div className="sd-legend">
                        <div className="item"><span className="sw sel"/>SELECTED</div>
                        <div className="item"><span className="sw hit"/>HIT</div>
                        <div className="item"><span className="sw sup"/>SUPERBALL</div>
                        <div className="item"><span className="sw missed"/>MISSED</div>
                    </div>
                    <div className="sd-howto">
                        Pick 2-10 · 10 balls drop · <strong>KEEP</strong> or <strong>DOUBLE UP</strong> · Ball 20 = SUPERBALL (×4)
                    </div>
                    {session && (
                        <div className="sd-free-plays-chip" data-testid="sd-free-plays">
                            <span>FREE PLAYS</span><strong>{session.free_plays_remaining}</strong>
                        </div>
                    )}
                </div>
            </div>

            {/* Bonus trigger / retrigger overlay */}
            {bonusOverlay && (
                <div className="keno-4h-overlay" data-testid="sd-bonus-overlay">
                    <div className="card sd-bonus-card">
                        <h1>{bonusOverlay.headline}</h1>
                        <p>{bonusOverlay.subtext}</p>
                        {bonusOverlay.freePlays > 0 && <div className="free-plays">+{bonusOverlay.freePlays}</div>}
                    </div>
                </div>
            )}

            {/* Bonus complete summary */}
            {gameState === STATE.BONUS_COMPLETE && bonusTotals && (
                <div className="keno-4h-overlay" data-testid="sd-bonus-complete">
                    <div className="keno-4h-modal-card sd-summary">
                        <h1>BONUS COMPLETE</h1>
                        <table>
                            <tbody>
                                <tr><td>Super Free Plays Played</td><td>{bonusTotals.played}</td></tr>
                                <tr><td>Superball Retriggers</td><td>{bonusTotals.retriggers}</td></tr>
                                <tr><td>Total Bonus Win</td><td style={{color:'#39ff14'}}>${bonusTotals.winnings.toFixed(2)}</td></tr>
                            </tbody>
                        </table>
                        <button className="sd-play-btn" style={{width:'100%', marginTop:14}} onClick={collectAndContinue} data-testid="sd-collect-continue">
                            <span className="sd-play-main">COLLECT & CONTINUE</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Win flash */}
            {winFlash && (
                <div className={`keno-4h-win-flash tier-${winFlash.tier}`} data-testid="sd-win-flash">
                    <div className="rays"/>
                    <div className="confetti">
                        {Array.from({length:24}).map((_,i)=><span key={i} className={`piece p${i%6}`} style={{['--i']:i}}/>)}
                    </div>
                    <div className="banner">
                        <div className="banner-label">{winFlash.tier==='mega'?'MEGA WIN!':winFlash.tier==='huge'?'HUGE WIN!':winFlash.tier==='big'?'BIG WIN!':'YOU WON!'}</div>
                        <div className="banner-amount">${Number(winFlash.amount).toLocaleString('en-US',{minimumFractionDigits:2, maximumFractionDigits:2})}</div>
                    </div>
                </div>
            )}

            {/* Help modal */}
            {showHelp && (
                <div className="keno-4h-overlay" onClick={()=>setShowHelp(false)} data-testid="sd-help-modal">
                    <div className="keno-4h-modal-card" onClick={(e)=>e.stopPropagation()}>
                        {[
                            {t:'PICK YOUR SPOTS', b:'Select 2 to 10 numbers. Choose your starting wager. Press PLAY GAME. The first 10 balls are drawn.'},
                            {t:'KEEP BET OR DOUBLE UP', b:'After 10 balls the round pauses. KEEP BET continues at your original wager. DOUBLE UP deducts one additional wager and doubles your active bet.'},
                            {t:'SUPERBALL & BONUS', b:'Ball 20 is the SUPERBALL. It multiplies your payout by 4× — but ONLY when the Superball is one of YOUR picked numbers. If it lands and you did not pick it, there is no 4×. DOUBLE UP + a Superball you picked = 5 Super Free Plays (retrigger awards +2). Progressive jackpot is separate.'},
                        ].map((p, i) => i === helpPage && (
                            <div key={i}>
                                <h1>{p.t}</h1>
                                <p style={{color:'#fff', fontSize:'0.95rem', lineHeight:1.5, margin:'12px 0 12px'}}>{p.b}</p>
                                {i === 2 && (
                                    <div className="sd-help-legend" data-testid="sd-help-legend">
                                        ⚡ SUPERBALL ×4 counts <strong>only</strong> when the last ball is one of <strong>YOUR</strong> picked numbers.
                                    </div>
                                )}
                            </div>
                        ))}
                        <div style={{display:'flex', gap:8, justifyContent:'space-between'}}>
                            <button className="sd-btn sd-btn-blue" style={{padding:'6px 12px'}} onClick={()=>setHelpPage(x=>Math.max(0,x-1))} disabled={helpPage===0} data-testid="sd-help-prev">← PREV</button>
                            <span style={{color:'#cfe1ff', alignSelf:'center'}}>{helpPage+1}/3</span>
                            <button className="sd-btn sd-btn-blue" style={{padding:'6px 12px'}} onClick={()=>setHelpPage(x=>Math.min(2,x+1))} disabled={helpPage===2} data-testid="sd-help-next">NEXT →</button>
                        </div>
                        <button className="sd-play-btn" style={{width:'100%', marginTop:14}} onClick={()=>{setShowHelp(false); setHelpPage(0);}}>
                            <span className="sd-play-main">EXIT HELP</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Paytable modal */}
            {showPaytable && (
                <div className="keno-4h-overlay" onClick={()=>setShowPaytable(false)} data-testid="sd-paytable-modal">
                    <div className="keno-4h-modal-card" onClick={(e)=>e.stopPropagation()} style={{textAlign:'center'}}>
                        <h1>PAYTABLE · ${bet.toFixed(2)}</h1>
                        <p style={{color:'#cfe1ff', fontSize:'0.75rem'}}>Spots: {paytableSpots} · Active: ${activeWagerDisplay.toFixed(2)} · Superball ×4 shown</p>
                        <table>
                            <thead><tr><th>HITS</th><th>BASE</th><th>SUPERBALL ×4</th></tr></thead>
                            <tbody>
                                {paytableRows.map(r=>(
                                    <tr key={r.hits}>
                                        <td style={{color:'#40c8ff'}}>{r.hits}</td>
                                        <td>${r.base}</td>
                                        <td style={{color:'#39ff14'}}>${r.sb}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p style={{fontSize:'0.7rem', color:'#cfe1ff', marginTop:8}}>Double Up changes the active wager. The Superball multiplies your final qualifying payout by 4× — but only when the Superball is one of YOUR picked numbers. Progressive prizes are separate.</p>
                        <button className="sd-play-btn" style={{width:'100%', marginTop:14}} onClick={()=>setShowPaytable(false)}>
                            <span className="sd-play-main">CLOSE</span>
                        </button>
                    </div>
                </div>
            )}

            {/* History modal — player's last 10 rounds */}
            {showHistory && (
                <div className="keno-4h-overlay" onClick={()=>setShowHistory(false)} data-testid="sd-history-modal">
                    <div className="keno-4h-modal-card" onClick={(e)=>e.stopPropagation()} style={{textAlign:'center', maxWidth:520, width:'94%'}}>
                        <h1>MY LAST 10 ROUNDS</h1>
                        {history === null ? (
                            <p style={{color:'#cfe1ff', margin:'18px 0'}}>Loading…</p>
                        ) : history.length === 0 ? (
                            <p style={{color:'#cfe1ff', margin:'18px 0'}}>No rounds yet — play a round to see it here.</p>
                        ) : (
                            <div className="sd-history-list" data-testid="sd-history-list">
                                {history.map((h) => {
                                    const open = expandedRound === h.round_id;
                                    const matchSet = new Set(h.matches || []);
                                    return (
                                    <div key={h.round_id} className={`sd-history-item ${open ? 'open' : ''}`}>
                                        <div
                                            className={`sd-history-row ${h.payout > 0 ? 'win' : ''}`}
                                            data-testid="sd-history-row"
                                            role="button"
                                            onClick={() => setExpandedRound(open ? null : h.round_id)}
                                        >
                                            <div className="sd-h-when">
                                                <span className="sd-h-caret">{open ? '▾' : '▸'}</span>
                                                {new Date(h.created_at).toLocaleString([], {month:'short', day:'numeric', hour:'numeric', minute:'2-digit'})}
                                                {h.round_type === 'SUPER_FREE_PLAY' && <span className="sd-h-tag">FREE</span>}
                                            </div>
                                            <div className="sd-h-hits">{h.hits}/{h.spots} hits{h.superball_hit && <span className="sd-h-sb"> ×4</span>}</div>
                                            <div className="sd-h-bet">${h.active_wager.toFixed(2)}</div>
                                            <div className={`sd-h-pay ${h.payout > 0 ? 'win' : ''}`}>{h.payout > 0 ? `+$${h.payout.toFixed(2)}` : '—'}</div>
                                        </div>
                                        {open && (
                                            <div className="sd-history-detail" data-testid="sd-history-detail">
                                                <div className="sd-hd-label">Your picks ({h.spots}) — gold = matched</div>
                                                <div className="sd-hd-balls">
                                                    {h.selected_numbers.map((n) => (
                                                        <span key={n} className={`sd-hd-ball ${matchSet.has(n) ? 'hit' : 'pick'}`}>{n}</span>
                                                    ))}
                                                </div>
                                                <div className="sd-hd-label">20 balls drawn — gold = your numbers · ⬤ = Superball</div>
                                                <div className="sd-hd-balls">
                                                    {h.drawn_numbers.map((n, i) => (
                                                        <span key={i} className={`sd-hd-ball ${matchSet.has(n) ? 'hit' : 'drawn'} ${i === 19 ? 'sup' : ''}`}>{n}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                        <p style={{fontSize:'0.7rem', color:'#cfe1ff', marginTop:10}}>Superball ×4 only counts when the last ball is one of your picked numbers.</p>
                        <button className="sd-play-btn" style={{width:'100%', marginTop:12}} onClick={()=>setShowHistory(false)} data-testid="sd-history-close">
                            <span className="sd-play-main">CLOSE</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Auto-Play Config Modal */}
            {showAutoConfig && (
                <div className="keno-4h-overlay" onClick={()=>setShowAutoConfig(false)} data-testid="sd-autoplay-modal">
                    <div className="keno-4h-modal-card sd-auto-modal" onClick={(e)=>e.stopPropagation()}>
                        <h1 className="sd-electric">AUTO-PLAY</h1>
                        <p className="sd-auto-caption">Automate the paid round loop. Bonus / Free Plays always pause the run.</p>

                        <div className="sd-auto-section">
                            <div className="sd-auto-label">DOUBLE-UP MODE</div>
                            <div className="sd-auto-choices">
                                {[
                                    {v:'keep', t:'KEEP BET', s:'Never double'},
                                    {v:'double', t:'DOUBLE UP', s:'Always double'},
                                    {v:'ask', t:'ASK ME', s:'Pause to decide'},
                                ].map(o => (
                                    <button
                                        key={o.v}
                                        className={`sd-auto-choice ${autoMode===o.v?'active':''}`}
                                        onClick={()=>setAutoMode(o.v)}
                                        data-testid={`sd-auto-mode-${o.v}`}
                                    >
                                        <span className="t">{o.t}</span>
                                        <span className="s">{o.s}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="sd-auto-section">
                            <div className="sd-auto-label">ROUNDS</div>
                            <div className="sd-auto-choices">
                                {[5, 10, 25, 50, 0].map(n => (
                                    <button
                                        key={n}
                                        className={`sd-auto-choice ${autoRounds===n?'active':''}`}
                                        onClick={()=>setAutoRounds(n)}
                                        data-testid={`sd-auto-rounds-${n}`}
                                    >
                                        <span className="t">{n === 0 ? '∞' : n}</span>
                                        <span className="s">{n === 0 ? 'until stop' : 'rounds'}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="sd-auto-section sd-auto-limits">
                            <div className="sd-auto-limit">
                                <label htmlFor="sd-stop-win">STOP IF WIN ≥</label>
                                <div className="sd-auto-input">
                                    <span>$</span>
                                    <input
                                        id="sd-stop-win"
                                        type="number"
                                        min="0" step="0.01"
                                        placeholder="off"
                                        value={autoStopWin}
                                        onChange={e=>setAutoStopWin(e.target.value)}
                                        data-testid="sd-auto-stop-win"
                                    />
                                </div>
                            </div>
                            <div className="sd-auto-limit">
                                <label htmlFor="sd-stop-bal">STOP IF BALANCE &lt;</label>
                                <div className="sd-auto-input">
                                    <span>$</span>
                                    <input
                                        id="sd-stop-bal"
                                        type="number"
                                        min="0" step="0.01"
                                        placeholder="off"
                                        value={autoStopBalance}
                                        onChange={e=>setAutoStopBalance(e.target.value)}
                                        data-testid="sd-auto-stop-balance"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="sd-auto-recap">
                            {autoMode==='keep' && 'Keep original wager after every 10 balls.'}
                            {autoMode==='double' && 'Double up on every round (deducts +bet each round). Falls back to KEEP if balance is low.'}
                            {autoMode==='ask' && 'Pause the run at the Double-Up decision so you can pick each time.'}
                        </div>

                        <div className="sd-auto-actions">
                            <button className="sd-btn sd-btn-red" onClick={()=>setShowAutoConfig(false)} data-testid="sd-auto-cancel">CANCEL</button>
                            <button
                                className="sd-play-btn"
                                onClick={startAutoPlay}
                                disabled={!canPlay}
                                data-testid="sd-auto-start"
                                style={{width:'auto', minWidth:180}}
                            >
                                <span className="sd-play-main">START</span>
                                <span className="sd-play-sub">{autoRounds === 0 ? '∞' : `${autoRounds}`} × ${bet.toFixed(2)}</span>
                            </button>
                        </div>
                        {!canPlay && (
                            <p className="sd-auto-warn">Pick 2-10 spots and make sure your balance covers the wager to start.</p>
                        )}
                    </div>
                </div>
            )}

            {/* Auto-Play running HUD */}
            {autoRunning && (
                <div className="sd-auto-hud" data-testid="sd-auto-hud">
                    <div className="sd-auto-hud-row">
                        <span className="lbl">MODE</span>
                        <strong>{autoMode==='keep'?'KEEP':autoMode==='double'?'DOUBLE':'ASK'}</strong>
                    </div>
                    <div className="sd-auto-hud-row">
                        <span className="lbl">ROUNDS LEFT</span>
                        <strong data-testid="sd-auto-remaining">{autoRemaining >= 9999 ? '∞' : autoRemaining}</strong>
                    </div>
                    <div className="sd-auto-hud-row">
                        <span className="lbl">P/L</span>
                        <strong className={autoTotals.won - autoTotals.wagered >= 0 ? 'pos' : 'neg'} data-testid="sd-auto-pl">
                            {autoTotals.won - autoTotals.wagered >= 0 ? '+' : '−'}${Math.abs(autoTotals.won - autoTotals.wagered).toFixed(2)}
                        </strong>
                    </div>
                    <button className="sd-btn sd-btn-red sd-auto-stop-btn" onClick={stopAutoPlay} data-testid="sd-auto-stop">STOP</button>
                </div>
            )}
        </div>
    );
};

export default SuperDoubleUp;
